import React, { useCallback, useContext, useMemo } from 'react';
import {
  makeStyles, Button, mergeClasses, tokens, shorthands,
} from '@fluentui/react-components';

const useCopilotTabItemStyles = makeStyles({
  active: {
    color: tokens.colorPaletteBlueBorderActive,
    ...shorthands.borderColor(tokens.colorPaletteBlueBorderActive),
    '&:hover': {
      color: tokens.colorPaletteBlueBorderActive,
      ...shorthands.borderColor(tokens.colorPaletteBlueBorderActive),
    },
    '@media (forced-colors: active)': {
      color: 'Highlight',
      ...shorthands.borderColor('Highlight'),
      '&:hover': {
        color: 'Highlight',
        ...shorthands.borderColor('Highlight'),
      },
      '&:focus': {
        color: 'Highlight',
        ...shorthands.borderColor('Highlight'),
      },
    },
  },
});

interface ITabItemProps {
  tabKey: string;
  children: React.ReactNode;
}

interface ICopilotTabContext {
  active?: string;
  onActiveChange?: (key: string) => void;
}

const CopilotTabContext = React.createContext<ICopilotTabContext>({});

export const CopilotTabItem: React.FC<ITabItemProps> = React.memo(({ tabKey, children }) => {
  const styles = useCopilotTabItemStyles();
  const { active, onActiveChange } = useContext(CopilotTabContext);

  const isActive = active === tabKey;
  const className = mergeClasses(isActive && styles.active, isActive && 'active', 'copilot-tab-item');
  const onClick = useCallback(() => {
    if (onActiveChange) {
      onActiveChange(tabKey);
    }
  }, [tabKey, onActiveChange]);

  return (
    <Button appearance="outline" className={className} onClick={onClick} aria-pressed={isActive}>
      {children}
    </Button>
  );
});

const useCopilotTabStyles = makeStyles({
  root: {
    '& .copilot-tab-item': {
      marginRight: tokens.spacingHorizontalSNudge,
      marginBottom: tokens.spacingVerticalSNudge,
      '&:last-child': {
        marginRight: 0,
      },
    },
  },
});

interface ICopilotTabProps {
  active: string;
  onActiveChange: ICopilotTabContext['onActiveChange'];
  children: React.ReactNode;
  className?: string;
}

export const CopilotTab: React.FC<ICopilotTabProps> = React.memo((props) => {
  const {
    active,
    onActiveChange,
    children,
    className,
  } = props;

  const styles = useCopilotTabStyles();

  const value = useMemo(() => ({ active, onActiveChange }), [active, onActiveChange]);

  return (
    <div className={mergeClasses(styles.root, 'copilot-tab', className)}>
      <CopilotTabContext.Provider value={value}>
        {children}
      </CopilotTabContext.Provider>
    </div>
  );
});
